import { Localized } from "@fluent/react"
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded"
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import {
    alpha,
    Box,
    ButtonBase,
    Chip,
    LinearProgress,
    Paper,
    Skeleton,
    Typography,
} from "@mui/material"

import type { DocumentWorkflowStageDto } from "../../documentWorkflowApi"
import {
    stageTypeChipIcons,
    stageTypeLabels,
    stageTypeStyles,
} from "../../DocumentWorkflowConfig"
import {
    formatDateOnly,
    getStageUiType,
    isPastDeadline,
    mapStageStatusToUi,
    mapWorkflowActionStatus,
    normalizeWorkflowEnum,
} from "../../documentWorkflowHelpers"

export type WorkflowStatusBarProps = {
    stages: DocumentWorkflowStageDto[]
    currentStage: DocumentWorkflowStageDto | null
    isLoading?: boolean
    onOpenWorkflow?: () => void
}

const getNearestDeadline = (stage: DocumentWorkflowStageDto) => {
    const deadlines = stage.members
        .filter(member => mapWorkflowActionStatus(member.actionStatus).waiting)
        .map(member => member.deadline)
        .filter((deadline): deadline is string => Boolean(deadline))
        .map(deadline => ({
            value: deadline,
            timestamp: new Date(deadline).getTime(),
        }))
        .filter(deadline => !Number.isNaN(deadline.timestamp))
        .sort((left, right) => left.timestamp - right.timestamp)

    return deadlines[0]?.value ?? null
}

export const WorkflowStatusBar = ({
    stages,
    currentStage,
    isLoading = false,
    onOpenWorkflow,
}: WorkflowStatusBarProps) => {
    if (isLoading) {
        return (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Skeleton variant="rounded" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton width={220} height={24} />
                        <Skeleton width="45%" height={18} />
                    </Box>
                </Box>
            </Paper>
        )
    }

    const workflowCompleted = stages.length > 0
        && currentStage === null
        && stages.every(
            stage => mapStageStatusToUi(stage.status).kind === "completed"
        )

    if (!currentStage) {
        const color = workflowCompleted ? "#2E7D32" : "#64748B"

        return (
            <Paper
                variant="outlined"
                sx={{
                    overflow: "hidden",
                    borderColor: alpha(color, 0.3),
                    bgcolor: alpha(color, 0.045),
                }}
            >
                <ButtonBase
                    onClick={onOpenWorkflow}
                    disabled={!onOpenWorkflow}
                    sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        px: { xs: 1.5, md: 2 },
                        py: 1.25,
                        textAlign: "left",
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            mr: 1.25,
                            borderRadius: 1.5,
                            color,
                            bgcolor: alpha(color, 0.1),
                        }}
                    >
                        {workflowCompleted
                            ? <CheckCircleOutlineRoundedIcon />
                            : <HourglassTopRoundedIcon />}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>
                            {workflowCompleted
                                ? "Workflow zakończony"
                                : "Workflow nie jest aktywny"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {workflowCompleted
                                ? `Zakończono wszystkie etapy (${stages.length}).`
                                : "Dokument nie ma obecnie aktywnego etapu."}
                        </Typography>
                    </Box>

                    {onOpenWorkflow && (
                        <ArrowForwardIosRoundedIcon
                            sx={{ ml: 1.5, fontSize: 17, color: "text.secondary" }}
                        />
                    )}
                </ButtonBase>
            </Paper>
        )
    }

    const stageType = getStageUiType(currentStage)
    const stageStyle = stageTypeStyles[stageType]
    const stageIndex = stages.findIndex(stage => stage.id === currentStage.id)
    const latestApprovalResult = [...currentStage.actions]
        .filter(action => normalizeWorkflowEnum(String(action.actionType)) === "APPROVE")
        .sort((left, right) => {
            const leftDate = new Date(left.execDate ?? left.createDate).getTime()
            const rightDate = new Date(right.execDate ?? right.createDate).getTime()
            return rightDate - leftDate
        })
        .map(action => String(action.actionResult ?? "").trim().toUpperCase())
        .find(result => result === "A" || result === "R")

    const completedMembers = currentStage.members.filter(
        member => mapWorkflowActionStatus(member.actionStatus).kind === "completed"
    )
    const waitingMembers = currentStage.members.filter(
        member => mapWorkflowActionStatus(member.actionStatus).waiting
    )
    const overdueMembers = waitingMembers.filter(
        member => isPastDeadline(member.deadline)
    )

    const progress = currentStage.members.length > 0
        ? Math.round(
            (completedMembers.length / currentStage.members.length) * 100
        )
        : 0

    const nearestDeadline = getNearestDeadline(currentStage)
    const nearestDeadlineOverdue = isPastDeadline(nearestDeadline)

    return (
        <Paper
            variant="outlined"
            sx={{
                overflow: "hidden",
                borderColor: alpha(stageStyle.color, 0.35),
                bgcolor: stageStyle.background,
                boxShadow: `0 4px 18px ${alpha(stageStyle.color, 0.07)}`,
            }}
        >
            <ButtonBase
                onClick={onOpenWorkflow}
                disabled={!onOpenWorkflow}
                sx={{
                    width: "100%",
                    justifyContent: "flex-start",
                    px: { xs: 1.5, md: 2 },
                    py: 1.25,
                    textAlign: "left",
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        mr: 1.25,
                        borderRadius: 1.5,
                        color: stageStyle.color,
                        bgcolor: alpha(stageStyle.color, 0.12),
                        border: "1px solid",
                        borderColor: alpha(stageStyle.color, 0.24),
                        "& svg": { fontSize: 23 },
                    }}
                >
                    {stageTypeChipIcons[stageType]}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                color: stageStyle.color,
                                fontWeight: 800,
                                lineHeight: 1.2,
                            }}
                        >
                            Bieżący etap
                            {stageIndex >= 0 && ` ${stageIndex + 1} z ${stages.length}`}
                        </Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={stageTypeLabels[stageType]}
                            sx={{
                                height: 21,
                                color: stageStyle.color,
                                borderColor: alpha(stageStyle.color, 0.4),
                                bgcolor: alpha("#FFFFFF", 0.5),
                                "& .MuiChip-label": {
                                    px: 0.75,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                },
                            }}
                        />
                        {latestApprovalResult && <Chip
                            size="small"
                            variant="outlined"
                            color={latestApprovalResult === "A" ? "success" : "error"}
                            label={<Localized
                                id={`document-workflow-action-status-${latestApprovalResult === "A" ? "approved" : "rejected"}`}
                            >
                                <span>{latestApprovalResult === "A" ? "APPROVED" : "REJECTED"}</span>
                            </Localized>}
                            sx={{
                                height: 21,
                                "& .MuiChip-label": {
                                    px: 0.75,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                },
                            }}
                        />}
                    </Box>

                    <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{ mt: 0.15, fontWeight: 750, lineHeight: 1.3 }}
                    >
                        {currentStage.name}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns: "145px auto auto",
                        alignItems: "center",
                        gap: 2,
                        ml: 2,
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.4,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Postęp
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {completedMembers.length}/{currentStage.members.length}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 6,
                                borderRadius: 999,
                                bgcolor: alpha(stageStyle.color, 0.12),
                                "& .MuiLinearProgress-bar": {
                                    bgcolor: stageStyle.color,
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Oczekujemy na
                        </Typography>
                        <Typography
                            variant="body2"
                            color={overdueMembers.length > 0 ? "error.main" : "text.primary"}
                            sx={{ fontWeight: 700 }}
                        >
                            {waitingMembers.length}
                            {overdueMembers.length > 0
                                && ` · ${overdueMembers.length} po terminie`}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Najbliższy termin
                        </Typography>
                        <Typography
                            variant="body2"
                            color={nearestDeadlineOverdue ? "error.main" : "text.primary"}
                            sx={{ fontWeight: 700 }}
                        >
                            {nearestDeadline
                                ? formatDateOnly(nearestDeadline)
                                : "Brak terminu"}
                        </Typography>
                    </Box>
                </Box>

                {onOpenWorkflow && (
                    <ArrowForwardIosRoundedIcon
                        sx={{ ml: 1.5, fontSize: 17, color: "text.secondary" }}
                    />
                )}
            </ButtonBase>
        </Paper>
    )
}